/**
 * Render whatever came back, guided by `output_schema`.
 *
 * Arrays of objects become tables, objects become key/value cards, and anything
 * that looks like a URL or an image becomes a link or a thumbnail. The schema is
 * a hint, not a contract we trust blindly — the real data wins when they
 * disagree, because a stale manifest should degrade to a readable view rather
 * than an empty one.
 */

import { useMemo, useState } from 'react'
import type { JsonSchema } from '@/core/types/api.types'
import { isPlainObject } from '@/core/utils/schema.utils'
import { CopyButton } from '@/shared/components'

interface Props {
  output: unknown
  schema?: JsonSchema
}

const MAX_ROWS = 200

export function SchemaOutput({ output, schema }: Props) {
  const [raw, setRaw] = useState(false)

  const json = useMemo(() => JSON.stringify(output, null, 2), [output])

  if (output === null || output === undefined) {
    return (
      <p className="muted small">
        The action succeeded and returned no content.
      </p>
    )
  }

  return (
    <div className="stack" style={{ gap: 10 }}>
      <div className="row">
        <div className="tabs" style={{ margin: 0, border: 'none' }}>
          <button className="tab" data-on={!raw} onClick={() => setRaw(false)} type="button">
            Formatted
          </button>
          <button className="tab" data-on={raw} onClick={() => setRaw(true)} type="button">
            Raw JSON
          </button>
        </div>
        <div className="spacer" />
        <CopyButton value={json} label="Copy JSON" />
      </div>

      {raw ? <pre className="json">{json}</pre> : <Formatted value={output} schema={schema} />}
    </div>
  )
}

function Formatted({ value, schema }: { value: unknown; schema?: JsonSchema }) {
  if (Array.isArray(value)) return <ArrayView rows={value} schema={schema} />
  if (isPlainObject(value)) return <ObjectView obj={value} />
  return <ScalarView value={value} />
}

// --- arrays -----------------------------------------------------------------

function ArrayView({ rows, schema }: { rows: unknown[]; schema?: JsonSchema }) {
  if (rows.length === 0) {
    return <p className="muted small">No records returned.</p>
  }

  const objectRows = rows.filter(isPlainObject)
  // A mixed or scalar array is not a table; a list is the honest rendering.
  if (objectRows.length !== rows.length) {
    return (
      <div className="table-wrap">
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th style={{ width: 60 }}>#</th>
                <th>Value</th>
              </tr>
            </thead>
            <tbody>
              {rows.slice(0, MAX_ROWS).map((row, i) => (
                <tr key={i}>
                  <td className="faint mono">{i + 1}</td>
                  <td>
                    <ScalarView value={row} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    )
  }

  // Column order: whatever the schema declares first, then any extra keys the
  // provider actually sent.
  const declared = Object.keys(schema?.items?.properties ?? {})
  const seen = new Set<string>()
  for (const row of objectRows.slice(0, 50)) {
    for (const key of Object.keys(row)) seen.add(key)
  }
  const columns = [
    ...declared.filter((c) => seen.has(c)),
    ...[...seen].filter((c) => !declared.includes(c)),
  ]

  const shown = objectRows.slice(0, MAX_ROWS)

  return (
    <div className="stack" style={{ gap: 6 }}>
      <div className="row small muted">
        <span>
          {rows.length} record{rows.length === 1 ? '' : 's'}
        </span>
        {rows.length > MAX_ROWS && (
          <span className="faint">· showing the first {MAX_ROWS}, use Raw JSON for all</span>
        )}
      </div>
      <div className="table-wrap">
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                {columns.map((col) => (
                  <th key={col}>{col.replace(/_/g, ' ')}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {shown.map((row, i) => (
                <tr key={i}>
                  {columns.map((col) => (
                    <td key={col}>
                      <ScalarView value={row[col]} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

// --- objects ----------------------------------------------------------------

function ObjectView({ obj }: { obj: Record<string, unknown> }) {
  const entries = Object.entries(obj)
  if (entries.length === 0) return <p className="muted small">Empty object.</p>

  return (
    <div className="card card-pad-0">
      <div className="kv">
        {entries.map(([key, value]) => (
          <Row key={key} label={key} value={value} />
        ))}
      </div>
    </div>
  )
}

function Row({ label, value }: { label: string; value: unknown }) {
  return (
    <>
      <div>{label.replace(/_/g, ' ')}</div>
      <div>
        {Array.isArray(value) || isPlainObject(value) ? (
          <NestedValue value={value} />
        ) : (
          <ScalarView value={value} />
        )}
      </div>
    </>
  )
}

function NestedValue({ value }: { value: unknown }) {
  const [open, setOpen] = useState(false)
  const count = Array.isArray(value) ? value.length : Object.keys(value as object).length
  const kind = Array.isArray(value) ? 'item' : 'key'

  // A long vector or a deep provider object would swamp the card; collapse by
  // default and let the reader opt in.
  if (Array.isArray(value) && value.length > 8 && value.every((v) => typeof v === 'number')) {
    return (
      <span className="muted small">
        {value.length} numbers ·{' '}
        <span className="mono">
          [{value.slice(0, 3).map((n) => Number(n).toFixed(4)).join(', ')} … ]
        </span>
      </span>
    )
  }

  return (
    <div>
      <button className="btn btn-ghost btn-sm" onClick={() => setOpen((v) => !v)} type="button">
        {open ? '▾' : '▸'} {count} {kind}
        {count === 1 ? '' : 's'}
      </button>
      {open && (
        <pre className="json" style={{ marginTop: 5 }}>
          {JSON.stringify(value, null, 2)}
        </pre>
      )}
    </div>
  )
}

// --- scalars ----------------------------------------------------------------

const IMAGE_EXT = /\.(png|jpe?g|gif|webp|svg)(\?|$)/i

function ScalarView({ value }: { value: unknown }) {
  if (value === null || value === undefined) return <span className="faint">—</span>

  if (typeof value === 'boolean') {
    return (
      <span className={`badge ${value ? 'badge-ok' : 'badge-neutral'}`}>
        {value ? 'true' : 'false'}
      </span>
    )
  }

  if (typeof value === 'number') return <span className="mono">{value.toLocaleString()}</span>

  if (typeof value === 'string') {
    if (/^https?:\/\//i.test(value)) {
      if (IMAGE_EXT.test(value)) {
        return (
          <a href={value} target="_blank" rel="noreferrer noopener">
            <img
              src={value}
              alt=""
              style={{ maxHeight: 34, borderRadius: 4, display: 'block' }}
            />
          </a>
        )
      }
      return (
        <a href={value} target="_blank" rel="noreferrer noopener" className="truncate">
          {value.replace(/^https?:\/\//, '')}
        </a>
      )
    }

    // ISO timestamps read far better localised.
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(value)) {
      const d = new Date(value)
      if (!Number.isNaN(d.getTime())) {
        return <span title={value}>{d.toLocaleString()}</span>
      }
    }

    if (value.length > 260) {
      return <ExpandableText text={value} />
    }
    return <span style={{ whiteSpace: 'pre-wrap' }}>{value}</span>
  }

  return <span className="mono">{JSON.stringify(value)}</span>
}

function ExpandableText({ text }: { text: string }) {
  const [open, setOpen] = useState(false)
  return (
    <div>
      <span style={{ whiteSpace: 'pre-wrap' }}>
        {open ? text : `${text.slice(0, 260)}…`}
      </span>{' '}
      <button className="btn btn-ghost btn-sm" onClick={() => setOpen((v) => !v)} type="button">
        {open ? 'less' : 'more'}
      </button>
    </div>
  )
}
